import { AfterViewInit, Component, DestroyRef, ElementRef, HostBinding, input, OnInit, ViewChild, inject } from '@angular/core'
import { Router } from '@angular/router'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Observable, debounceTime, of } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { SearchService } from 'src/app/core/services/search.service'
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { CommonModule } from '@angular/common'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { EventService } from 'src/app/core/services/event.service'
import { ChipsService } from 'src/app/core/services/chips.service'
import { scrollDetector } from 'src/app/core/utils/scroll-detector.util'
import { RoutePaths } from 'src/app/core/data/route-paths.data'
import { SearchRoutePaths } from 'src/app/core/data/search-route-paths.data'
import { ISearchResults } from 'src/app/core/interfaces/search.interface'
import { StorageService } from 'src/app/core/services/storage.service'
import { GlobalDataService } from 'src/app/core/services/global-data.service'

export interface ISearchItem {
  value: string
  value_id?: string
  key: string
  count: number | string
  children?: ISearchItem[]
}

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
  ],
})
export class SearchComponent implements OnInit, AfterViewInit {

  private _destroyRef = inject(DestroyRef)
  private _searchService = inject(SearchService)
  private _router = inject(Router)
  private _storageService = inject(StorageService)
  private _globalDataService = inject(GlobalDataService)
  protected chipsService = inject(ChipsService)
  protected eventService = inject(EventService)

  myControl = new FormControl('')
  options: string[] = []
  filteredOptions: ISearchItem[][] | undefined
  limit = 20
  searchText = ''

  @HostBinding('class.is-stuck') isStuck: boolean = false
  isSticky = input<boolean>(false)
  withFilters = input<boolean>(false)

  @ViewChild('searchInput') searchInput!: ElementRef

  ngOnInit(): void {
    this.myControl.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(500),
      switchMap(value => {
        this.filteredOptions = undefined
        this.searchText = value || ''
        if (!value || value.length < 2) {
          return of([])
        }
        return this.getSearchResults(value)
      })
    ).subscribe((results: any) => {
      this.setResults(results.results?.[0] ?? [])
    })
  }

  ngAfterViewInit(): void {
    this.tryFocusSearchOnLoad()
    this.tryOpenMakeOnLoad()
    this.tryOpenCategoryOnLoad()
  }

  tryFocusSearchOnLoad() {
    const focusSearchOnLoad = this._storageService.getItem('focusSearchOnLoad', 'session')
    if (focusSearchOnLoad) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus()
        this._storageService.removeItem('focusSearchOnLoad', 'session')
      }, 0)
    }
  }

  tryOpenMakeOnLoad() {
    const openMakeOnLoad = this._storageService.getItem('openMakeOnLoad', 'session')
    if (openMakeOnLoad) {
      this._globalDataService.searchExpandMake.set(true)
      this._storageService.removeItem('openMakeOnLoad', 'session')
    }
  }

  tryOpenCategoryOnLoad() {
    const openCategoryOnLoad = this._storageService.getItem('openCategoryOnLoad', 'session')
    if (openCategoryOnLoad) {
      this._globalDataService.searchExpandCategory.set(true)
      this._storageService.removeItem('openCategoryOnLoad', 'session')
    }
  }

  onScroll($event: Event) {
    this.isStuck = this.isSticky() && scrollDetector()
  }

  getSearchResults(term: string): Observable<ISearchResults> {
    return this._searchService.getSearchResults(term, this.limit)
  }

  setResults(results: any): void {
    if (!results) {
      return
    }
    const resultsKeys = Object.keys(results)
    this.filteredOptions = resultsKeys
      .filter(key => !!results[key])
      .map((key) => {
        return results[key].map((item: any) => ({ ...item, key, value: item.value }))
      })
      .filter(group => {
        return group.length > 0
      })
  }

  onSelect( event: MatAutocompleteSelectedEvent ): void {
    const selection: ISearchItem = event.option.value
    const url = this.buildRedirectUrl(selection)
    this.myControl.setValue('')
    this._router.navigate( [ url ], {})
  }

  getLabel( item: ISearchItem[] ): string {
    // spilt on underscore start and capitalize each word
    return (item[0]?.key ?? '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace('Display ', '')
  }

  buildRedirectUrl(item: ISearchItem): string {
    if (item.key === 'display_equipment_number') {
      return `${RoutePaths.Details}${item.value_id ?? item.value}`
    }
    let extras = ''
    switch (item.key) {
      case 'category': {
        const category = item.value
        extras = SearchRoutePaths.Category.replace(':category', category)
        break
      }
      case 'subcategory': {
        const subcategory = item.value
        const category = item.children?.[0].value ?? ''
        extras = SearchRoutePaths.Subcategory.replace(':category', category).replace(':subcategory', subcategory)
        break
      }
      case 'make': {
        const make = item.value
        extras = SearchRoutePaths.Make.replace(':make', make)
        break
      }
      case 'model': {
        const model = item.value
        const make = item.children?.[0].value ?? ''
        extras = SearchRoutePaths.Model.replace(':make', make).replace(':model', model)
        break
      }
      case 'branch_country': {
        const country = item.value
        extras = SearchRoutePaths.Country.replace(':country', country)
        break
      }
      case 'branch_state': {
        const state = item.value
        const country = item.children?.[0].value ?? ''
        extras = SearchRoutePaths.State.replace(':country', country).replace(':state', state)
        break
      }
      case 'branch_city': {
        const city = item.value
        const state = item.children?.[0].value ?? ''
        const country = item.children?.[0].children?.[0].value ?? ''
        extras = SearchRoutePaths.City.replace(':country', country).replace(':state', state).replace(':city', city)
        break
      }
    }
    return `${RoutePaths.Search}${extras}`
  }
}
